// RITA-1601 (Funde 63 und 128, Wortlaut und Lage von Julia am 04.11.2026 freigegeben) — Gründe für eine Rückfrage vor
// dem Druck, als reine Regeln ohne React. Die Rückfrage selbst zeigt components/DruckDialoge.tsx (DruckRueckfrageDialog);
// das Fenster „Vertrag erstellen“ und die Bearbeiten-Seite stellen hier ihre Gründe zusammen.
//
// Gründe (jeder für sich eine eigene Rückfrage; kommen mehrere zusammen, eine einzige mit dem Titel „Bitte vor dem
// Druck prüfen“):
// - fehlendes Dokument (RITA-1599, nur Fenster): Für Vertrag oder Anschreiben passt keine Vorlage.
// - schon ein Vertrag gedruckt (Fund 128, nur Bearbeiten-Seite): Ein Druck MIT Vertrag ersetzt den zuletzt gedruckten
//   Vertrag – auch einen unterschriebenen, auch aus einem älteren Entwurf. Gefragt wird, solange die Person das Ersetzen
//   genau dieses Vertrags noch nicht bestätigt hat (in der Karte mit „Trotzdem neuen erstellen“ oder hier mit „Trotzdem
//   drucken“); druckt inzwischen jemand anderes einen neuen Vertrag, ist das ein anderer Vertrag, und es wird wieder
//   gefragt. Nur das Anschreiben ersetzt nichts (Entscheidung 4 aus RITA-1549).
// - Vertragsinhaber fehlt (Fund 63): Beim Berater ist ein abweichender Vertragsinhaber eingetragen, aber sein Name steht
//   nicht im Vertrag, der gedruckt würde (Kopfzeile, Text, Fußzeile). Im Fenster sind das die Vorlage mit eingesetzten
//   Werten, auf der Seite der sichtbare Text – von Hand ergänzt heißt: keine Warnung. Ohne abweichenden Vertragsinhaber
//   gibt es diese Prüfung nicht; sie gilt nur, wenn der Vertrag angehakt ist.
// - fehlende Angaben (RITA-1552): die bekannte Liste.
import type { Druckeintrag } from '../api/vertragsdruck';
import type { Block, Inlineknoten, Vertragsdokument } from '../../templates/contractEditor/model/types';
import type { FehlendesDokument } from './druckdokumente';
import { letzterVertragsdruck, scanVorhandenFuer } from './vertragsbloecke';

/** Fund 128: der Vertrag, den dieser Druck ersetzen würde, mit den Angaben für die Rückfrage. */
export interface SchonGedrucktGrund {
  /** Kennung des Protokolleintrags; „Trotzdem drucken“ merkt sie sich als bestätigt. */
  eintragId: number;
  /** created_at des Drucks mit Vertrag. */
  datumIso: string;
  /** Zu diesem Vertrag ist schon ein unterschriebener Scan hochgeladen. */
  scanVorhanden: boolean;
  /** Der gespeicherte Entwurf ist älter als dieser Vertrag. */
  entwurfAelter: boolean;
}

/** Alle Gründe einer Rückfrage; `zeilen` ist die Liste der fehlenden Angaben (leer = keine). */
export interface Druckgruende {
  fehlendesDokument?: FehlendesDokument | null;
  schonGedruckt?: SchonGedrucktGrund | null;
  /** Name des abweichenden Vertragsinhabers, der im Vertrag fehlt. */
  vertragsinhaber?: string | null;
  zeilen: string[];
}

/** Zahl der Gründe – ab zwei trägt die Rückfrage den Titel „Bitte vor dem Druck prüfen“. */
export function anzahlGruende(gruende: Druckgruende): number {
  return [
    !!gruende.fehlendesDokument,
    !!gruende.schonGedruckt,
    !!(gruende.vertragsinhaber ?? '').trim(),
    gruende.zeilen.length > 0,
  ].filter(Boolean).length;
}

export const hatGruende = (gruende: Druckgruende): boolean => anzahlGruende(gruende) > 0;

function inlineText(inhalt: Inlineknoten[]): string {
  return inhalt
    .map((knoten) => {
      if (knoten.typ === 'text') return knoten.text;
      if (knoten.typ === 'platzhalter') return `{{${knoten.schluessel}}}`;
      // Kästchen und Zeilenumbruch trennen Wörter.
      return ' ';
    })
    .join('');
}

function blockText(bloecke: Block[]): string[] {
  return bloecke.flatMap((block) => {
    if (block.typ === 'absatz') return [inlineText(block.inhalt)];
    if (block.typ === 'liste') return block.eintraege.map(inlineText);
    return [];
  });
}

/** Leerraum (auch Zeilenumbrüche) auf einzelne Leerzeichen gebracht, damit ein Name über Formatwechsel hinweg gefunden wird. */
const normalisiert = (wert: string) => wert.replace(/\s+/g, ' ').trim();

/**
 * Steht der Name im Dokument – in Kopfzeile, Text oder Fußzeile? Verglichen wird der sichtbare Text; benachbarte
 * Textläufe (etwa ein fetter Vorname) zählen zusammen, Leerraum zählt nicht. Ein leerer Name steht nie im Dokument.
 */
export function nameImDokument(dokument: Vertragsdokument, name: string): boolean {
  const gesucht = normalisiert(name);
  if (!gesucht) return false;
  const text = normalisiert(
    [...blockText(dokument.kopf.inhalt), ...blockText(dokument.text), ...blockText(dokument.fuss.inhalt)].join('\n'),
  );
  return text.includes(gesucht);
}

/**
 * Fund 63: Ist ein abweichender Vertragsinhaber eingetragen (`inhaber` nicht leer) und steht sein Name nicht im Vertrag?
 * `vertrag` ist das Dokument, das gedruckt würde: im Fenster die Vorlage mit eingesetzten Werten, auf der Seite der
 * sichtbare Editorstand.
 */
export function vertragsinhaberFehlt(vertrag: Vertragsdokument, inhaber: string | null | undefined): boolean {
  const name = (inhaber ?? '').trim();
  return name !== '' && !nameImDokument(vertrag, name);
}

const zeitwert = (iso: string | null | undefined): number => (iso ? new Date(iso).getTime() : NaN);

/**
 * Ist der gespeicherte Entwurf älter als der Vertrag? `gespeichertAm` ist der Speicherstand der Sitzung (null = noch nie
 * gespeichert, also gerade erst aus den Vorlagen begonnen – nicht älter). Unlesbare Zeiten gelten nicht als älter.
 */
export function entwurfAelterAls(gespeichertAm: string | null | undefined, vertragIso: string): boolean {
  const entwurf = zeitwert(gespeichertAm);
  const vertrag = zeitwert(vertragIso);
  return !Number.isNaN(entwurf) && !Number.isNaN(vertrag) && entwurf < vertrag;
}

/**
 * Fund 128: Der Grund „Schon ein Vertrag gedruckt“ für einen Druck mit Vertrag von der Bearbeiten-Seite – aus dem beim
 * Klick frisch geladenen Aktionsprotokoll. Null, wenn kein Vertrag im PRM gedruckt ist oder die Person das Ersetzen genau
 * dieses Vertrags schon bestätigt hat (`bestaetigt` = Kennung aus der Karte oder aus dieser Seite).
 */
export function schonGedrucktGrund(
  eintraege: Druckeintrag[],
  bestaetigt: number | null,
  gespeichertAm: string | null | undefined,
): SchonGedrucktGrund | null {
  const letzter = letzterVertragsdruck(eintraege);
  if (!letzter || letzter.id === bestaetigt) return null;
  return {
    eintragId: letzter.id,
    datumIso: letzter.created_at,
    scanVorhanden: scanVorhandenFuer(eintraege, letzter.id),
    entwurfAelter: entwurfAelterAls(gespeichertAm, letzter.created_at),
  };
}
